import { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { NpsMonthlyResult } from '@/types';
import './NpsTrendChart.css';

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

interface NpsTrendChartProps {
  data: NpsMonthlyResult[];
}

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
};

function formatMonth(ym: string): string {
  const [year, month] = ym.split('-');
  const label = MONTH_LABELS[month] ?? month;
  return `${label} ${year?.slice(2)}`;
}

function npsColor(score: number): string {
  if (score < 0) return '#ef4444';
  if (score < 50) return '#f59e0b';
  return '#22c55e';
}

export function NpsTrendChart({ data }: NpsTrendChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<echarts.EChartsType | null>(null);

  const months = useMemo(() => data.map(d => formatMonth(d.month)), [data]);
  const scores = useMemo(() => data.map(d => d.score), [data]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current, undefined, { renderer: 'canvas' });
    instanceRef.current = chart;

    const option: echarts.EChartsCoreOption = {
      backgroundColor: 'transparent',
      grid: { top: 16, right: 16, bottom: 28, left: 40 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(17,24,39,0.95)',
        borderColor: 'rgba(100,166,247,0.4)',
        textStyle: { color: '#f1f5f9', fontSize: 12 },
        formatter: (params: unknown) => {
          const p = (params as Array<{ name: string; value: number }>)?.[0];
          if (!p) return '';
          const color = npsColor(p.value);
          return `<span style="color:${color};font-weight:700">${p.value}</span> NPS<br/><span style="opacity:0.6">${p.name}</span>`;
        },
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        axisLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: -100,
        max: 100,
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
        axisLine: { show: false },
        axisLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10 },
      },
      series: [{
        type: 'line',
        data: scores,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 3, color: '#3b82f6' },
        itemStyle: {
          color: (params: { value: number }) => npsColor(params.value as number),
          borderColor: '#1e293b',
          borderWidth: 2,
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(59,130,246,0.25)' },
            { offset: 1, color: 'rgba(59,130,246,0.02)' },
          ]),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: 'rgba(255,255,255,0.15)', type: 'dashed', width: 1 },
          data: [{ yAxis: 0 }],
          label: { show: false },
        },
      }],
    };

    chart.setOption(option);

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
      chart.dispose();
      instanceRef.current = null;
    };
  }, [months, scores]);

  if (data.length === 0) {
    return (
      <div className="nps-trend nps-trend--empty">
        <p>No NPS data yet</p>
      </div>
    );
  }

  return <div ref={chartRef} className="nps-trend" aria-label="NPS trend chart" />;
}
